import {useMemo} from 'react'
import {announce} from '@github-ui/aria-live'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {getEnv} from '@github-ui/client-env'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {agentTaskPinsQueryKey, parseTaskPins} from '../utils/task-pins'
import {transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {getRepoIdsNeedingFetch} from './useTasks'
import {usePullRequests} from './usePullRequests'
import {useRepoInfo} from './use-repo-info'
import type {UITask} from '../types/task'
import {useCurrentUserOptional} from '../contexts/CurrentUserContext'

const PIN_REFETCH_INTERVAL = 30_000

export function useTaskPins(enabled = true, poll = false) {
  const queryClient = useQueryClient()
  const currentUserLogin = useCurrentUserOptional()?.currentUser?.login ?? getEnv().login
  const queryKey = [...agentTaskPinsQueryKey, getEnv().cmcApiUrl ?? 'default', currentUserLogin]
  const query = useQuery({
    queryKey,
    queryFn: async ({signal, queryKey: fetchingQueryKey}) => {
      if (
        queryClient.getQueryData(fetchingQueryKey) === undefined &&
        queryClient.getQueryState(fetchingQueryKey)?.fetchFailureCount === 0
      ) {
        announce('Loading pinned sessions.')
      }
      const response = await fetchCAPI({path: '/agents/pins', signal})
      return parseTaskPins(await response.json())
    },
    enabled: enabled && copilotFeatureFlags.taskPinning,
    staleTime: 10_000,
    refetchOnMount: poll ? 'always' : true,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: enabled && poll && copilotFeatureFlags.taskPinning ? PIN_REFETCH_INTERVAL : false,
    retry: (count, error) =>
      count < 2 &&
      (error instanceof TypeError ||
        (error instanceof FetchCAPIError && (error.status === 429 || error.status >= 500))),
  })
  return {...query, queryKey}
}

export function usePinnedSidebarTasks(enabled: boolean) {
  const queryClient = useQueryClient()
  const query = useTaskPins(enabled, true)
  const tasks = useMemo(
    () =>
      enabled && copilotFeatureFlags.taskPinning
        ? (query.data ?? []).flatMap(pin => {
            const task = transformAPITaskToUnhydrated(pin.task)
            return task
              ? [{...task, event_type: pin.task.event_type ?? undefined, event_identifiers: pin.task.event_identifiers}]
              : []
          })
        : [],
    [enabled, query.data],
  )
  const prTasks = useMemo(
    () => tasks.filter(task => task.resource_id !== undefined && task.resource_id !== -1),
    [tasks],
  )
  const pulls = usePullRequests({tasks: prTasks})
  const repoIds = useMemo(() => getRepoIdsNeedingFetch(tasks, pulls.data), [tasks, pulls.data])
  const repositories = useRepoInfo(repoIds)
  const hydratedTasks = useMemo<UITask[]>(
    () =>
      tasks.map(task => {
        const pull = task.resource_id ? pulls.data[task.resource_id] : undefined
        const repository = task.repo_id ? repositories.data[task.repo_id] : undefined
        return {
          ...task,
          pull,
          nwo: pull?.repository_nwo ?? (repository ? `${repository.ownerLogin}/${repository.name}` : undefined),
          repositoryName: repository?.name,
        }
      }),
    [tasks, pulls.data, repositories.data],
  )
  return {
    tasks: hydratedTasks,
    isLoading: query.isLoading || pulls.isLoading || repositories.isLoading,
    isError: query.isError,
    hasHydrationError:
      pulls.isError || repositories.isError || (!repositories.isLoading && repoIds.some(id => !repositories.data[id])),
    refetch: async () => {
      await Promise.all([
        query.refetch(),
        pulls.refetch(),
        ...repoIds.map(id => queryClient.invalidateQueries({queryKey: ['repo-info', id]})),
      ])
    },
  }
}
